/**
 * Translates Fera review text (DE -> EN) wherever it appears on shapetape.eu:
 *  - the grid/masonry "all reviews" widget
 *  - the built-in "Show More" review modal
 *  - a custom click-triggered popup that mounts its own Fera widget
 *    (e.g. data-fera-widget="all_reviews" inside #fera-reviews-popup)
 *
 * Fera's review body markup takes two different shapes depending on where
 * it renders:
 *   1. Grid card:  [data-fera-component="body"] contains separate
 *      .fera-truncatedText--truncated and .fera-truncatedText--full children.
 *   2. Modal / popup: [data-fera-component="body"] itself IS the full text
 *      (no separate truncated/full children).
 * This script handles both.
 *
 * It also translates the linked product name (data-fera-component=
 * "productName") and rewrites the "View product" link's host from
 * shapetape.de to shapetape.eu, so the link doesn't bounce a .eu visitor
 * back to the .de storefront.
 *
 * NOTE ON PRODUCT SLUGS: this script does NOT machine-translate the URL
 * slug (the /products/xxxxx path). Only the host is swapped. If your .eu
 * store actually has separate English product handles (via Shopify
 * Markets/locales), those need to come from Shopify's own localized URL
 * data (e.g. Storefront API `product.url` in the en context), not from
 * running the slug through Google Translate — a translated slug string
 * will very likely not match a real product handle and will 404. If you
 * want that wired up properly, that's a separate piece (a small handle
 * lookup/map), happy to help with it once you confirm whether .eu has
 * real English handles.
 *
 * It also watches document.body (not just the main widget container) so it
 * catches review markup injected anywhere — including modals and popups
 * added after the fact — the moment it appears.
 *
 * Add near the closing </body> tag, or in the section rendering the reviews.
 * Scope to the .eu domain in Liquid:
 *   {% if request.host contains 'shapetape.eu' %}
 *     <script src="{{ 'fera-review-translate.js' | asset_url }}" defer></script>
 *   {% endif %}
*/

(function () {
  'use strict';

  var SOURCE_LANG = 'de';
  var TARGET_LANG = 'en';
  // Free, no-key endpoint — fine for review volume. Swap for DeepL/Google
  // Cloud Translate if you start hitting rate limits.
  var TRANSLATE_ENDPOINT = 'https://translate.googleapis.com/translate_a/single';

  // Host to rewrite "View product" links from -> to. Only the host is
  // swapped; the path (product slug) is left untouched. See note above.
  var SOURCE_HOST = 'shapetape.de';
  var TARGET_HOST = 'shapetape.eu';

  var cache = new Map();

  function translateText(text) {
    var trimmed = (text || '').trim();
    if (!trimmed) return Promise.resolve(text);
    if (cache.has(trimmed)) return Promise.resolve(cache.get(trimmed));

    var url = TRANSLATE_ENDPOINT +
      '?client=gtx&sl=' + SOURCE_LANG + '&tl=' + TARGET_LANG +
      '&dt=t&q=' + encodeURIComponent(trimmed);

    return fetch(url)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var translated = data[0].map(function (chunk) { return chunk[0]; }).join('');
        cache.set(trimmed, translated);
        return translated;
      })
      .catch(function (err) {
        console.error('[fera-translate] failed:', err);
        return text;
      });
  }

  function translateReviewBody(bodyEl) {
    if (!bodyEl || bodyEl.getAttribute('data-translated') === 'true') return;

    var truncatedEl = bodyEl.querySelector('.fera-truncatedText--truncated');
    var fullEl = bodyEl.querySelector('.fera-truncatedText--full');
    var jobs = [];

    if (truncatedEl || fullEl) {
      // Grid card shape: separate truncated/full children.
      if (fullEl) {
        jobs.push(
          translateText(fullEl.textContent).then(function (t) {
            fullEl.textContent = t;
          })
        );
      }
      if (truncatedEl) {
        var showMoreLink = truncatedEl.querySelector('a.fera-truncatedText-showMore');
        var linkHTML = showMoreLink ? showMoreLink.outerHTML : '';
        var linkText = showMoreLink ? showMoreLink.textContent : '';
        var truncatedOnly = truncatedEl.textContent.replace(linkText, '').trim();
        jobs.push(
          translateText(truncatedOnly).then(function (t) {
            truncatedEl.innerHTML = t + (linkHTML ? ' ' + linkHTML : '');
          })
        );
      }
    } else {
      // Modal / popup shape: the body element itself holds the full text.
      jobs.push(
        translateText(bodyEl.textContent).then(function (t) {
          bodyEl.textContent = t;
        })
      );
    }

    Promise.all(jobs).then(function () {
      bodyEl.setAttribute('data-translated', 'true');
    });
  }

  function translateProductName(nameEl) {
    if (!nameEl || nameEl.getAttribute('data-translated') === 'true') return Promise.resolve();
    return translateText(nameEl.textContent).then(function (t) {
      nameEl.textContent = t;
      nameEl.setAttribute('data-translated', 'true');
    });
  }

  function rewriteProductLinkHost(linkEl) {
    if (!linkEl || linkEl.getAttribute('data-host-rewritten') === 'true') return;
    try {
      var url = new URL(linkEl.href);
      if (url.hostname === SOURCE_HOST) {
        url.hostname = TARGET_HOST;
        linkEl.href = url.toString();
      }
    } catch (err) {
      console.error('[fera-translate] could not parse product link:', err);
    }
    linkEl.setAttribute('data-host-rewritten', 'true');
  }

  function translateReview(reviewEl) {
    translateReviewBody(reviewEl.querySelector('[data-fera-component="body"]'));
    translateProductName(reviewEl.querySelector('[data-fera-component="productName"]'));
    rewriteProductLinkHost(reviewEl.querySelector('.fera-review-product-link'));
  }

  function scan(root) {
    if (root.matches && root.matches('.fera-review')) {
      translateReview(root);
    }
    if (root.querySelectorAll) {
      root.querySelectorAll('.fera-review').forEach(translateReview);
    }
  }

  function init() {
    // Translate anything already on the page (initial widget render).
    scan(document.body);

    // Watch the whole document for reviews added later: grid pagination,
    // the "Show More" modal, and your custom popup's Fera widget mount.
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          scan(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();